import { useContext, useEffect, useState } from 'react';
import { cs } from '@bbkAdminUtils/css-utils';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import { getSelectedAddonsForPlan } from '@bbkPricingPage/helpers/addon-helper';
import {
  GroupedBy,
  type Item,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';
import { CustomisePlan } from './components/customise-plan';
import { OrderSummary } from './components/order-summary';
import { PlanSelector } from './components/plan-selector';

export type ActiveItemPrice = {
  item: Item;
  price: Price;
  isSubscribedItem: boolean;
  isSubscribedPrice: boolean;
};
export type PlanAddonsMapValue = Map<
  string,
  { addonId: string; quantity: number }
>;
export type PlanAddonsMap = {
  [planKey: string]: PlanAddonsMapValue;
};

export const PlanCustomisation = ({
  activeItemPrices,
}: {
  activeItemPrices: ActiveItemPrice[];
}) => {
  const { pricingTable, discounts } = useContext(PricingTableContext);
  const addons = pricingTable?.addons ?? [];
  const groupedBy = pricingTable?.grouped_by ?? GroupedBy.item;

  const planPrices = activeItemPrices.filter(
    (aip) => !aip.item.customization.is_custom_amount_text
  );
  const noOfPlans = planPrices.length;

  const [selectedPlanPrice, setSelectedPlanPrice] = useState<
    ActiveItemPrice | undefined
  >(planPrices[0]);
  const [plansSelectedAddons, setPlansSelectedAddons] = useState<PlanAddonsMap>(
    {}
  );
  const selectedPlan = selectedPlanPrice?.item;
  const selectedItemPrice = selectedPlanPrice?.price;

  useEffect(() => {
    if (planPrices.length === 0) {
      setSelectedPlanPrice(undefined);
      return;
    }

    if (!selectedPlan || !selectedItemPrice) {
      setSelectedPlanPrice(planPrices[0]);
      return;
    }

    const planPrice =
      groupedBy === GroupedBy.item
        ? planPrices.find(
            (aip) =>
              aip.item.billing_system_id === selectedPlan.billing_system_id
          )
        : planPrices.find(
            (aip) =>
              aip.price.period === selectedItemPrice.period &&
              aip.price.period_unit === selectedItemPrice.period_unit &&
              aip.item.billing_system_id === selectedPlan.billing_system_id
          );
    setSelectedPlanPrice(planPrice ?? planPrices[0]);
  }, [
    planPrices,
    groupedBy,
    selectedPlan,
    selectedItemPrice,
    selectedPlanPrice,
  ]);

  if (
    !selectedPlanPrice ||
    !!pricingTable?.customization?.hide_plan_customization
  ) {
    return null;
  }
  const selectedAddons = getSelectedAddonsForPlan(
    selectedItemPrice?.billing_system_id as string,
    plansSelectedAddons
  );

  if (
    !activeItemPrices ||
    !pricingTable?.customization?.show_addons_and_charges ||
    noOfPlans === 0 ||
    !addons.length
  ) {
    return null;
  }

  return (
    <div className="tw-w-full tw-pt-20">
      <div className={cs('tw-mx-auto', noOfPlans <= 3 && 'tw-max-w-6xl')}>
        {/* Heading Section */}
        <div className="tw-mb-6 tw-text-center">
          <div className="tw-text-3xl tw-font-bold !tw-text-[var(--pricing-page-headline-color)]">
            <RenderAlignedHtml
              value={
                pricingTable?.customization?.plan_customization?.heading ?? ''
              }
              className="[&_.slate-p]:tw-leading-8"
            />
          </div>
          <div className="tw-mt-2 !tw-text-[color-mix(in_srgb,var(--pricing-page-headline-color)_50%,transparent)]">
            <RenderAlignedHtml
              value={
                pricingTable?.customization?.plan_customization?.sub_heading ??
                ''
              }
              className="[&_.slate-p]:tw-leading-6"
            />
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div
        className={
          'tw-overflow-hidden tw-rounded-pricingtable-border-radius tw-border tw-border-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_20%,transparent)] ' +
          '!tw-bg-[var(--pricing-page-card-background-color)] tw-shadow-sm'
        }
      >
        <div className="tw-flex tw-flex-col @lg:tw-flex-row">
          <div
            className={cs(
              'tw-border-b tw-border-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_20%,transparent)] tw-p-8 tw-text-pricingtable-text-base @lg:tw-w-2/3 @lg:tw-border-b-0 @lg:tw-border-r'
            )}
          >
            <PlanSelector
              planPrices={planPrices}
              selectedPlanPrice={selectedPlanPrice}
              setSelectedPlanPrice={setSelectedPlanPrice}
            />
            <CustomisePlan
              selectedPlanPrice={selectedPlanPrice}
              plansSelectedAddons={plansSelectedAddons}
              setPlansSelectedAddons={setPlansSelectedAddons}
              applicableDiscounts={discounts}
            />
          </div>
          {selectedPlan && (
            <OrderSummary
              selectedPlanPrice={selectedPlanPrice}
              selectedAddons={selectedAddons}
              applicableDiscounts={discounts?.filter(
                (discount) =>
                  discount.item_price_id ===
                  selectedItemPrice?.billing_system_id
              )}

              // subscription={subscription}
            />
          )}
        </div>
      </div>
    </div>
  );
};
